import { Dispatch } from "react";
import i18n from "i18next";
import { OperatingArea, toOperatingAreaDetails } from "@optimusride/client-library/@types/grpc/site";
import { addAlert, Alert } from "../Alerts/alertsSlice";
import { apis } from "../common/utils/auth";
import {
    setStationInfo, setUser, setSiteDetails, SELECTED_SITE_LOCAL_STORAGE_KEY, Stations, UserRoles,
    sortAndRemapStationList,
} from "./userSlice";
import { captureException } from "../common/utils/sentry";
import { getInitials, getQueryParam } from "../common/utils";

// For all the areas the user can access we grab the stations for those operating areas
// If we cant grab them we display an error message to the user
const getStationInfo = async (
    dispatch: Dispatch<ReturnType<typeof setStationInfo | typeof addAlert>>,
    operatingAreaIDs: OperatingArea[],
) => {
    if (operatingAreaIDs.length === 0) return;

    const notLoadedOperatingAreas: string[] = [];

    // Loops through the operating area and fetches data for those operating areas
    const stationResponses = await Promise.all(
        operatingAreaIDs.map(async ({ name, id }) => {
            try {
                // fetchs the station list for the operating area
                const stationsListResponse = await apis.station.getStations(id);

                const stations = stationsListResponse?.toObject().stationsList || [];

                // sorts and remaps/flattens the stations to a custom interface for use in optifleet
                const sortedAndMappedStations = sortAndRemapStationList(stations);

                return { [id]: sortedAndMappedStations };
            } catch (e) {
                notLoadedOperatingAreas.push(name);
                captureException(e);
                return { [id]: [] };
            }
        }),
    );

    // If there are operating areas that were not loaded for some reason,
    // we send an error message to the user with that information
    if (notLoadedOperatingAreas.length !== 0) {
        dispatch(
            addAlert({
                text: i18n.t("user.stationErrorMessage", {
                    operatingAreas: notLoadedOperatingAreas.join(", "),
                }),
                type: "error",
            }),
        );
    }

    dispatch(
        setStationInfo(
            stationResponses.reduce(
                (curr, station) => ({
                    ...curr,
                    ...station,
                }),
                {},
            ) as Stations,
        ),
    );
};

export const getUserDetails = async (
    dispatch: Dispatch<ReturnType<typeof setUser | typeof addAlert | typeof setSiteDetails>>,
    user: firebase.default.User | null,
): Promise<boolean> => {
    // If there is no user we go back to the login page and dont fetch data
    if (!user) return false;

    const errorMessage: Alert = {
        text: i18n.t("user.errorMessage"),
        type: "error",
    };

    try {
        // Fetches the user profile
        const { userSummary } = (await apis.user.getUserProfile()).toObject();
        // Gets all the operating areas the user has access to
        const operatingAreasListResponse = await apis.site.getAllOperatingAreaDetails();

        const displayName = `${userSummary?.firstName} ${userSummary?.lastName}`;

        const operatingAreaIdsList = userSummary?.operatingAreaIdsList || [];

        // Filters the operating area list with the user list in user profile
        // then maps it to an object
        const filteredOperatingAreas = operatingAreasListResponse
            .getOperatingAreasList()
            .filter((operatingArea) => operatingAreaIdsList
                .includes(operatingArea.getOperatingAreaId()))
            .map(toOperatingAreaDetails);

        // Gets either thee query param from the url or the
        // last site the user has accessed stored in local storage
        // and sets that as the first site after the user logged in
        const queryParamStationInitials = getQueryParam("site");
        const previouslySelectedSite = window.localStorage.getItem(SELECTED_SITE_LOCAL_STORAGE_KEY);

        let selectedSite = filteredOperatingAreas?.[0];

        if (filteredOperatingAreas) {
            // NOTE: Query params > localstorage > default of first site
            if (queryParamStationInitials) {
                selectedSite = filteredOperatingAreas.find(
                    ({ name }) => getInitials(name) === queryParamStationInitials,
                ) || selectedSite;
            } else if (previouslySelectedSite) {
                const previouslySelectedID = parseInt(previouslySelectedSite, 10);
                selectedSite = filteredOperatingAreas.find(
                    ({ id }) => id === previouslySelectedID,
                ) || selectedSite;
            }
        }

        let commandorRole: UserRoles | undefined;

        // Gets the commandor role from the user id token,
        // this is used to show or hide certain elements of the ui
        try {
            commandorRole = (await user.getIdTokenResult())?.claims.commandor_role as UserRoles;
        } catch (e) {
            captureException(e);
        }

        dispatch(
            setUser({
                photoURL: user?.photoURL || undefined,
                displayName,
                commandorRole,
            }),
        );

        dispatch(
            setSiteDetails({
                siteOptions: filteredOperatingAreas,
                selectedSite,
            }),
        );

        // After setting the user details and the selected site
        // we asynchronously fetch details related to each operating area
        getStationInfo(dispatch, filteredOperatingAreas);

        return true;
    } catch (e) {
        captureException(e);
        dispatch(addAlert(errorMessage));

        return false;
    }
};
